(() => {
  const dialogSelector = '[data-my-product-ar-dialog]';
  const openParameter = 'open_ar';
  const controllers = new WeakMap();
  let deepLinkHandled = false;
  let modelViewerFeaturePromise = null;

  function loadModelViewerFeature() {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (modelViewerFeaturePromise) return modelViewerFeaturePromise;

    modelViewerFeaturePromise = new Promise((resolve, reject) => {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        reject(new Error('Shopify model viewer loader is unavailable.'));
        return;
      }

      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (errors) => {
            if (errors) {
              reject(new Error('Shopify model viewer failed to load.'));
              return;
            }

            if (customElements.get('model-viewer')) {
              resolve();
              return;
            }

            const timeoutId = window.setTimeout(() => {
              reject(new Error('Shopify model viewer definition timed out.'));
            }, 10000);

            customElements.whenDefined('model-viewer').then(() => {
              window.clearTimeout(timeoutId);
              resolve();
            });
          },
        },
      ]);
    });

    return modelViewerFeaturePromise;
  }

  function normalizeVariantId(value) {
    return value === null || value === undefined ? '' : String(value);
  }

  function normalizeModelUrl(value) {
    if (typeof value !== 'string' || value.trim() === '') return '';

    try {
      const url = new URL(value.trim(), window.location.origin);
      return url.protocol === 'https:' ? url.toString() : '';
    } catch (error) {
      return '';
    }
  }

  function getModelFilename(value) {
    const normalizedUrl = normalizeModelUrl(value);
    if (!normalizedUrl) return '';

    try {
      const pathname = new URL(normalizedUrl).pathname;
      return decodeURIComponent(pathname.split('/').pop() || '').toLowerCase();
    } catch (error) {
      return '';
    }
  }

  class MyCustomProductArDialog {
    constructor(dialog) {
      this.dialog = dialog;
      this.openers = Array.from(document.querySelectorAll(`[aria-controls="${dialog.id}"]`));
      this.closeButton = dialog.querySelector('.my-custom-product-ar-dialog__close');
      this.modelViewer = dialog.querySelector('[data-my-product-ar-model]');
      this.loadingMessage = dialog.querySelector('[data-my-product-ar-loading]');
      this.modelError = dialog.querySelector('[data-my-product-ar-model-error]');
      this.qrContainer = dialog.querySelector('[data-my-product-ar-qr]');
      this.qrFallback = dialog.querySelector('[data-my-product-ar-qr-fallback]');
      this.launchButton = dialog.querySelector('[data-my-product-ar-launch]');
      this.unsupportedMessage = dialog.querySelector('[data-my-product-ar-unsupported]');
      this.previousFocus = null;
      this.qrUrl = '';
      this.isOpen = false;
      this.modelViewerReady = Boolean(customElements.get('model-viewer'));

      const config = this.readConfig();
      this.variantOrder = config.variantOrder;
      this.shopifyModels = this.normalizeShopifyModels(config.shopifyModels);
      this.models = this.normalizeModels(config.models);
      this.firstModel = this.findFirstModel();
      this.currentVariantId = this.getInitialVariantId();
      this.currentModel = this.resolveModel(this.currentVariantId);

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleModelLoad = this.handleModelLoad.bind(this);
      this.handleModelError = this.handleModelError.bind(this);
      this.handleArStatus = this.handleArStatus.bind(this);

      if (!this.firstModel || !this.modelViewer) {
        this.openers.forEach((opener) => {
          opener.hidden = true;
        });
        return;
      }

      this.bindEvents();
      this.ensureModelViewer();
    }

    readConfig() {
      const configElement = document.getElementById(this.dialog.dataset.modelConfigId || '');
      if (!configElement) return { models: {}, shopifyModels: [], variantOrder: [] };

      try {
        const config = JSON.parse(configElement.textContent || '{}');
        let models = config.models || {};

        if (typeof models === 'string') {
          models = JSON.parse(models || '{}');
        }

        return {
          models: models && typeof models === 'object' ? models : {},
          shopifyModels: Array.isArray(config.shopifyModels) ? config.shopifyModels : [],
          variantOrder: Array.isArray(config.variantOrder)
            ? config.variantOrder.map(normalizeVariantId)
            : [],
        };
      } catch (error) {
        console.error('[3D/AR] モデル設定JSONを解析できませんでした。', error);
        return { models: {}, shopifyModels: [], variantOrder: [] };
      }
    }

    normalizeShopifyModels(rawShopifyModels) {
      const shopifyModels = new Map();

      rawShopifyModels.forEach((media) => {
        if (!media || !Array.isArray(media.sources)) return;

        const glbSource = media.sources.find((source) => {
          return source?.format === 'glb' || source?.mime_type === 'model/gltf-binary';
        });
        const usdzSource = media.sources.find((source) => {
          return source?.format === 'usdz' || source?.mime_type === 'model/vnd.usdz+zip';
        });
        const glbSrc = normalizeModelUrl(glbSource?.url);
        const filename = getModelFilename(glbSrc);

        if (!filename || !glbSrc || shopifyModels.has(filename)) return;

        shopifyModels.set(filename, {
          mediaId: normalizeVariantId(media.id),
          glbSrc,
          usdzSrc: normalizeModelUrl(usdzSource?.url),
        });
      });

      return shopifyModels;
    }

    normalizeModels(rawModels) {
      const models = new Map();

      Object.entries(rawModels || {}).forEach(([variantId, value]) => {
        if (!value || typeof value !== 'object') return;

        const configuredGlbSrc = normalizeModelUrl(value.glbSrc);
        if (!configuredGlbSrc) {
          if (value.glbSrc) {
            console.warn(`[3D/AR] バリアント ${variantId} の glbSrc は有効な HTTPS URL ではありません。`);
          }
          return;
        }

        const shopifyModel = this.shopifyModels.get(getModelFilename(configuredGlbSrc));

        models.set(normalizeVariantId(variantId), {
          variantId: normalizeVariantId(variantId),
          mediaId: shopifyModel?.mediaId || '',
          glbSrc: shopifyModel?.glbSrc || configuredGlbSrc,
          usdzSrc: normalizeModelUrl(value.usdzSrc || value.uzdzSrc) || shopifyModel?.usdzSrc || '',
        });
      });

      return models;
    }

    findFirstModel() {
      for (const variantId of this.variantOrder) {
        if (this.models.has(variantId)) return this.models.get(variantId);
      }

      return this.models.values().next().value || null;
    }

    getInitialVariantId() {
      return normalizeVariantId(
        window.optionPicker?.currentVariantId
        || window.myCustomSaveVariantId
        || this.dialog.dataset.currentVariantId
      );
    }

    resolveModel(variantId) {
      return this.models.get(normalizeVariantId(variantId)) || this.firstModel;
    }

    ensureModelViewer() {
      loadModelViewerFeature()
        .then(() => {
          this.modelViewerReady = true;
          if (this.isOpen) this.loadModel();
        })
        .catch((error) => {
          console.error('[3D/AR] 3Dビューアーを読み込めませんでした。', error);
          this.handleModelError();
        });
    }

    bindEvents() {
      this.openers.forEach((opener) => {
        opener.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.open();
        });
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-my-product-ar-close]')) {
          this.close();
        }
      });

      this.launchButton?.addEventListener('click', () => this.activateAr());
      this.modelViewer?.addEventListener('load', this.handleModelLoad);
      this.modelViewer?.addEventListener('error', this.handleModelError);
      this.modelViewer?.addEventListener('ar-status', this.handleArStatus);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('myOptionChange', this.handleVariantChange);
    }

    open() {
      if (this.isOpen || !this.currentModel) return;

      this.isOpen = true;
      this.previousFocus = document.activeElement;
      this.dialog.hidden = false;
      this.dialog.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('my-custom-product-ar-is-open');
      document.body.classList.add('my-custom-product-ar-is-open');

      this.loadModel();
      this.renderQrCode();

      window.requestAnimationFrame(() => {
        this.closeButton?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.dialog.hidden = true;
      this.dialog.setAttribute('aria-hidden', 'true');

      if (typeof this.modelViewer?.pause === 'function') {
        this.modelViewer.pause();
      }

      const anotherDialogIsOpen = Array.from(document.querySelectorAll(dialogSelector)).some(
        (dialog) => !dialog.hidden
      );

      if (!anotherDialogIsOpen) {
        document.documentElement.classList.remove('my-custom-product-ar-is-open');
        document.body.classList.remove('my-custom-product-ar-is-open');
      }

      if (this.previousFocus instanceof HTMLElement) {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    loadModel() {
      if (!this.currentModel || !this.modelViewer) return;

      if (!this.modelViewerReady) {
        this.setLoadingState();
        this.ensureModelViewer();
        return;
      }

      const currentSrc = this.modelViewer.getAttribute('src');
      const currentIosSrc = this.modelViewer.getAttribute('ios-src') || '';

      if (
        currentSrc === this.currentModel.glbSrc
        && currentIosSrc === this.currentModel.usdzSrc
      ) {
        if (this.modelViewer.loaded) this.handleModelLoad();
        return;
      }

      this.setLoadingState();

      if (this.currentModel.usdzSrc) {
        this.modelViewer.setAttribute('ios-src', this.currentModel.usdzSrc);
      } else {
        this.modelViewer.removeAttribute('ios-src');
      }

      this.modelViewer.setAttribute('src', this.currentModel.glbSrc);
    }

    setLoadingState() {
      if (this.loadingMessage) this.loadingMessage.hidden = false;
      if (this.modelError) this.modelError.hidden = true;
      if (this.unsupportedMessage) this.unsupportedMessage.hidden = true;
      if (this.launchButton) this.launchButton.disabled = true;
    }

    handleModelLoad() {
      if (this.loadingMessage) this.loadingMessage.hidden = true;
      if (this.modelError) this.modelError.hidden = true;

      window.setTimeout(() => this.updateArAvailability(), 100);
    }

    handleModelError(event) {
      console.error('[3D/AR] 3Dモデルの読み込みに失敗しました。', {
        variantId: this.currentModel?.variantId || '',
        mediaId: this.currentModel?.mediaId || '',
        glbSrc: this.currentModel?.glbSrc || '',
        detail: event?.detail || null,
      });
      if (this.loadingMessage) this.loadingMessage.hidden = true;
      if (this.modelError) this.modelError.hidden = false;
      if (this.unsupportedMessage) this.unsupportedMessage.hidden = true;
      if (this.launchButton) this.launchButton.disabled = true;
    }

    updateArAvailability() {
      if (!this.launchButton || !this.modelViewer) return;

      const hasActivateMethod = typeof this.modelViewer.activateAR === 'function';
      this.launchButton.disabled = !hasActivateMethod;
      if (this.unsupportedMessage) this.unsupportedMessage.hidden = hasActivateMethod;
    }

    activateAr() {
      if (
        !this.modelViewer
        || typeof this.modelViewer.activateAR !== 'function'
        || this.launchButton?.disabled
      ) {
        if (this.unsupportedMessage) this.unsupportedMessage.hidden = false;
        return;
      }

      if (this.unsupportedMessage) this.unsupportedMessage.hidden = true;

      try {
        const activation = this.modelViewer.activateAR();
        if (activation && typeof activation.catch === 'function') {
          activation.catch(() => {
            if (this.unsupportedMessage) this.unsupportedMessage.hidden = false;
          });
        }
      } catch (error) {
        if (this.unsupportedMessage) this.unsupportedMessage.hidden = false;
      }
    }

    handleArStatus(event) {
      const status = event.detail?.status;

      if (status === 'failed') {
        if (this.unsupportedMessage) this.unsupportedMessage.hidden = false;
      } else if (status === 'session-started') {
        if (this.unsupportedMessage) this.unsupportedMessage.hidden = true;
      }
    }

    handleVariantChange(event) {
      this.currentVariantId = normalizeVariantId(event.detail?.variantId);
      this.dialog.dataset.currentVariantId = this.currentVariantId;
      this.currentModel = this.resolveModel(this.currentVariantId);
      this.qrUrl = '';

      if (this.isOpen) {
        this.loadModel();
        this.renderQrCode();
      }
    }

    buildQrUrl() {
      const url = new URL(window.location.href);

      url.search = '';
      url.hash = '';

      if (this.currentVariantId) {
        url.searchParams.set('variant', this.currentVariantId);
      }

      url.searchParams.set(openParameter, '1');
      return url.toString();
    }

    renderQrCode() {
      if (!this.qrContainer) return;

      const nextQrUrl = this.buildQrUrl();
      if (this.qrUrl === nextQrUrl && this.qrContainer.childElementCount > 0) return;

      this.qrUrl = nextQrUrl;
      this.qrContainer.replaceChildren();

      if (typeof window.QRCode === 'function') {
        new window.QRCode(this.qrContainer, {
          text: nextQrUrl,
          width: 220,
          height: 220,
          colorDark: '#000000',
          colorLight: '#ffffff',
          imageAltText: 'AR表示用の商品ページQRコード',
        });

        this.qrContainer.querySelectorAll('img').forEach((image) => {
          image.alt = 'AR表示用の商品ページQRコード';
        });

        if (this.qrFallback) this.qrFallback.hidden = true;
        return;
      }

      if (this.qrFallback) {
        const link = this.qrFallback.querySelector('a');
        if (link) link.href = nextQrUrl;
        this.qrFallback.hidden = false;
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]):not([tabindex="-1"]), a[href], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  function getDialogs(scope) {
    const dialogs = [];

    if (scope instanceof Element && scope.matches(dialogSelector)) {
      dialogs.push(scope);
    }

    dialogs.push(...scope.querySelectorAll(dialogSelector));
    return dialogs;
  }

  function consumeDeepLink(controller) {
    if (deepLinkHandled || !controller) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get(openParameter) !== '1') return;

    deepLinkHandled = true;
    controller.open();
    url.searchParams.delete(openParameter);

    try {
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      // Shopify preview modes can restrict history changes; the dialog still opens normally.
    }
  }

  function initialize(scope = document) {
    const initializedControllers = getDialogs(scope).map((dialog) => {
      if (!controllers.has(dialog)) {
        controllers.set(dialog, new MyCustomProductArDialog(dialog));
      }
      return controllers.get(dialog);
    });

    const firstController = initializedControllers[0]
      || controllers.get(document.querySelector(dialogSelector));
    consumeDeepLink(firstController);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
